import { useState, useCallback } from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/services/auth.service';
import { handleApiError } from '@/utils/errorHandler';

export interface UseForgotPasswordResult {
  // Form state
  email: string;
  setEmail: (value: string) => void;
  
  // Validation errors
  emailError: string;
  
  // Loading state
  loading: boolean;
  
  // Error state
  error: string | null;
  
  // Actions
  handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  handleGoBack: () => void;
}

/**
 * Hook xử lý logic quên mật khẩu
 * - Validate email
 * - Gửi OTP đến email
 * - Điều hướng đến trang xác thực OTP
 */
export const useForgotPassword = (): UseForgotPasswordResult => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const navigate = useNavigate();

  /**
   * Validate email format
   * - Kiểm tra email không rỗng
   * - Kiểm tra format email hợp lệ (có @ và domain)
   */
  const validateEmail = useCallback((emailValue: string): boolean => {
    if (!emailValue) {
      setEmailError('Vui lòng nhập email!');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailValue)) {
      setEmailError('Email không hợp lệ!');
      return false;
    }
    setEmailError('');
    return true;
  }, []);

  /**
   * Xử lý thay đổi email
   * - Cập nhật email state
   * - Validate lại nếu đã có lỗi trước đó
   */
  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    if (emailError) {
      validateEmail(newEmail);
    }
  }, [emailError, validateEmail]);

  /**
   * Xử lý submit form quên mật khẩu
   * - Validate email
   * - Gọi API forgotPassword để gửi OTP
   * - Điều hướng đến trang xác thực OTP với email trong URL params
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    const isEmailValid = validateEmail(email);
    
    if (!isEmailValid) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      await authService.forgotPassword(email);
      message.success('Mã OTP đã được gửi đến email của bạn!');
      setEmail(''); // Clear email sau khi gửi thành công
      navigate(`/otp-verification?email=${encodeURIComponent(email)}`);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || 'Gửi mã OTP thất bại. Vui lòng thử lại.';
      handleApiError(err, errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [email, validateEmail, navigate]);

  /**
   * Điều hướng quay lại trang đăng nhập
   */
  const handleGoBack = useCallback(() => {
    navigate('/login');
  }, [navigate]);

  return {
    email,
    setEmail,
    emailError,
    loading,
    error,
    handleEmailChange,
    handleSubmit,
    handleGoBack,
  };
};

